import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import Input from "../../components/form/Input";
import labels from "../../constants/displayFields";
import ImageInput from "../../components/form/ImageInput";
import ImagesGroupedAsset from "./Images/ImagesGroupedAsset";
import GroupedAvailableQuantityField from "../../components/modals/InventoryTransactionMultipleModal/quantityCalculation/GroupedAvailableQuantityField";
import useAuth from "../../hooks/useAuth";
import { useGroupedAsset } from "../../contexts/grouped-asset";

export default function FormFieldsGroupedAsset({
  disabledForm,
  asset,
  createImage,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const { setFieldValue, values } = useFormikContext();
  const { groupedAsset } = useGroupedAsset();
  const currentAsset = groupedAsset?.id ? groupedAsset : asset;

  useEffect(() => {
    if (!values.itemCode && currentAsset.itemCode) {
      setFieldValue("itemCode", currentAsset.itemCode);
    }
  }, [currentAsset.itemCode, setFieldValue, values.itemCode]);

  const handleCreateImage = (file, { onSuccess, onFailure } = {}) => {
    return createImage({
      variables: { assetId: currentAsset.id, image: file },
      onSuccess,
      onFailure,
    });
  };

  return (
    <Card>
      <CardContent>
        <Box mt={-1} mb={1}>
          <Typography fontWeight={500} fontSize={18}>
            Grouped Item Info
          </Typography>
        </Box>
        <Grid container alignItems="flex-start" spacing={1}>
          <Grid container spacing={1} size={{ md: 6, xs: 12 }}>
            <Grid size={12}>
              <ImagesGroupedAsset disabled={disabledForm} asset={currentAsset} />
            </Grid>
            {!disabledForm && (
              <Grid size={12}>
                <ImageInput imageUrl={currentAsset.photo} onUpload={handleCreateImage} allowLibrary />
              </Grid>
            )}
          </Grid>
          <Grid container spacing={1} size={{ md: 6, xs: 12 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label={settings.filter_field1}
                value={currentAsset.client?.name || ""}
                disabled
                sx={{
                  fieldset: { borderColor: "primary.main" },
                  label: { color: "primary.main" },
                  input: { color: "primary.main" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label={settings.filter_field2}
                value={currentAsset.project?.name || ""}
                disabled
                sx={{
                  fieldset: { borderColor: "primary.main" },
                  label: { color: "primary.main" },
                  input: { color: "primary.main" },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                size="small"
                label="SKU ID"
                disabled
                value={currentAsset.itemId}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input
                fullWidth
                name="skuNumber"
                label="SKU Number"
                disabled={disabledForm}
                type="autoExpand"
              />
            </Grid>
            <Grid size={12}>
              <Input
                type="autoExpand"
                fullWidth
                name="description"
                label={labels.description}
                disabled={disabledForm}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <GroupedAvailableQuantityField asset={currentAsset} />
            </Grid>
            <Grid size={{ xs: 12, sm: 12 }}>
              <TextField
                size="small"
                label="Quantity in Warehouse"
                disabled
                value={currentAsset.groupedMemberAssets?.total_available_quantity || 0}
                fullWidth
              />
            </Grid>
            {/*}
              {settings.reservations && (
               <Grid item xs={12} sm={6}>
                 <TextField
                   size="small"
                   label="Reserved Quantity"
                   disabled
                   value={currentAsset.reservedQuantity || 0}
                   fullWidth
                 />
               </Grid>
              )}
              {*/}
            <Grid size={12}>
              <Box>
                <Box
                  sx={{
                    px: 1,
                    display: "flex",
                    justifyContent: "center",
                    height: 36.5,
                    borderRadius: "4px",
                    border: "1px solid rgba(0, 0, 0, 0.23)",
                    "& label": {
                      display: "flex",
                      flexDirection: "row-reverse",
                      justifyContent: "center",
                      mx: 0,
                      height: 36.5,
                      "& > span > p": {
                        lineHeight: 1,
                        color: "text.primary",
                      },
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.serializedGroupedAsset || false}
                        onChange={e => {
                          setFieldValue("serializedGroupedAsset", e.target.checked);
                        }}
                        disabled={disabledForm}
                        sx={{ p: 1 }}
                      />
                    }
                    label={
                      <Typography variant="body2" color="textPrimary">
                        Serialized Grouped Item
                      </Typography>
                    }
                  />
                </Box>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  When enabled, warehouse will manually assign specific serialized assets during
                  fulfillment. When disabled, system will auto-assign using FIFO at checkout.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
